/* eslint-disable react/no-unused-prop-types */
// this is used because all the links are actually used
// inside a function and to keep the naming somewhere it's stll saved here
import React, { useEffect, useMemo, useState } from 'react';

import { MapTo, Utils } from '@adobe/aem-react-editable-components';
import { useMutation } from '@apollo/client';
import { UPDATE_INSTALLER_CONSENT_MUTATION } from 'Queries/Mutations';
import { createPatchEinwilligungByInstallerIdData } from 'Queries/QueriesHelper';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { DataPolicyPopup } from 'components/DataPolicyPopup/DataPolicyPopup';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { RenderAemComponent } from 'components/RenderAemComponent/RenderAemComponent';

import { parseAEMDataPolicySHNGProps, parseAEMDatapolicyProps } from 'utils/AEMPropUtils';
import { booleanToGerman } from 'utils/DataUtils';

import { DataPolicyContent } from './DataPolicyContent';

import './DataPolicy.sass';

export const DataPolicy = (props) => {
  const { isLoading: isInstallerLoading, installer, refetchInstaller } = useCurrentInstaller();
  const [dsgvoAddressPublic, setDsgvoAddressPublic] = useState(false);
  const [dsgvoCanSendMail, setDsgvoCanSendMail] = useState(false);
  const [response, setResponse] = useState('');
  const [openSubmitPopup, setOpenSubmitPopup] = useState(false);
  const [updateInstallerConsentMutation] = useMutation(UPDATE_INSTALLER_CONSENT_MUTATION);
  const isEditor = Utils.isInEditor();

  useEffect(() => {
    if (isEditor) return;

    if (!isInstallerLoading && installer) {
      if (installer.zuAdressveroeff === 'Ja') {
        setDsgvoAddressPublic(true);
      }
      if (installer.zuInfoversand === 'Ja') {
        setDsgvoCanSendMail(true);
      }
    }
  }, [installer, isEditor, isInstallerLoading]);

  useEffect(() => {
    if (isEditor) return;

    setResponse('');
  }, [dsgvoAddressPublic, dsgvoCanSendMail, isEditor]);

  const submitButtonDisabled = useMemo(() => {
    if (isEditor) return true; // Default to disabled in editor mode

    return (
      openSubmitPopup ||
      isInstallerLoading ||
      (booleanToGerman(dsgvoAddressPublic) === installer?.zuAdressveroeff &&
        booleanToGerman(dsgvoCanSendMail) === installer?.zuInfoversand)
    );
  }, [
    isInstallerLoading,
    openSubmitPopup,
    dsgvoAddressPublic,
    dsgvoCanSendMail,
    installer,
    isEditor,
  ]);

  if (isEditor) {
    return <RenderAemComponent componentProps={props} name="DataPolicy" />;
  }

  const onSubmitValues = async () => {
    try {
      const responseData = await updateInstallerConsentMutation({
        variables: createPatchEinwilligungByInstallerIdData(dsgvoAddressPublic, dsgvoCanSendMail),
      });

      if (responseData.errors) {
        throw new Error(responseData.errors);
      }

      if (responseData.data.updateInstallerConsent.status !== 200) {
        throw new Error(
          'Mutation error with status: ',
          responseData.data.updateInstallerConsent.status
        );
      }

      setDsgvoAddressPublic(responseData.data.updateInstallerConsent.zuAdressveroeff === 'Ja');
      setDsgvoCanSendMail(responseData.data.updateInstallerConsent.zuInfoversand === 'Ja');
      setOpenSubmitPopup(true);
      refetchInstaller();
    } catch (e) {
      setResponse('Fehler beim Speichern');
    }
  };

  return (
    <div className="data-policy">
      {!installer || isInstallerLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <DataPolicyContent
            dsgvoAddressPublic={dsgvoAddressPublic}
            dsgvoCanSendMail={dsgvoCanSendMail}
            dsgvoAddressPublicName="zuAdressveroeff"
            dsgvoCanSendMailName="zuInfoversand"
            onChangeDsvgoAddressPublic={(event) => {
              setDsgvoAddressPublic(event.target.value);
            }}
            onChangeDsvgoCanSendMail={(event) => {
              setDsgvoCanSendMail(event.target.value);
            }}
            documents={parseAEMDatapolicyProps(props)}
            shngDocuments={parseAEMDataPolicySHNGProps(props)}
          />
          <div className="data-policy__response-message">{response}</div>
          <div className="data-policy__save-button-wrapper">
            <ActionButton
              disabled={submitButtonDisabled}
              title="Speichern"
              inverted
              type="button"
              onClick={onSubmitValues}
            />
          </div>
          <DataPolicyPopup
            openSubmitPopup={openSubmitPopup}
            setOpenSubmitPopup={setOpenSubmitPopup}
          />
        </>
      )}
    </div>
  );
};

// Look on top for eslint rule explanation

DataPolicy.propTypes = {
  datapolicyEDILink: PropTypes.string,
  datapolicyAVALink: PropTypes.string,
  datapolicySHNGLink: PropTypes.string,
  datapolicyBAGLink: PropTypes.string,
  datapolicyEENGLink: PropTypes.string,
  datapolicyHANSGLink: PropTypes.string,
  datapolicyNNNGLink: PropTypes.string,
  datapolicyEDILabel: PropTypes.string,
  datapolicyAVALabel: PropTypes.string,
  datapolicySHNGLabel: PropTypes.string,
  datapolicyBAGLabel: PropTypes.string,
  datapolicyEENGLabel: PropTypes.string,
  datapolicyHANSGLabel: PropTypes.string,
  datapolicyNNNGLabel: PropTypes.string,
};

DataPolicy.defaultProps = {
  datapolicyEDILink: '',
  datapolicyAVALink: '',
  datapolicySHNGLink: '',
  datapolicyBAGLink: '',
  datapolicyEENGLink: '',
  datapolicyHANSGLink: '',
  datapolicyNNNGLink: '',
  datapolicyEDILabel: '',
  datapolicyAVALabel: '',
  datapolicySHNGLabel: '',
  datapolicyBAGLabel: '',
  datapolicyEENGLabel: '',
  datapolicyHANSGLabel: '',
  datapolicyNNNGLabel: '',
};

MapTo(['e-fix/components/content/datapolicy'])(DataPolicy);
